import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NOTIFICATION_TYPE_ERROR, NotificationService } from 'ui-components/services/notification-service';

import { useUtcFormattedDate } from '../../../../../../hooks/use-datetime-formats';
import messages from '../../../../messages';
import type { AuditFormData } from '../audit-form-modal/audit-form-modal';
import { AuditFormModal } from '../audit-form-modal/audit-form-modal';
import type { StockAuditDetailsResponse } from '../../stock-audits-api';
import { getStockAudit } from '../../stock-audits-api';
import { AuditLoadingModal } from './audit-loading-modal';
import { AuditReadOnlyModal } from './audit-read-only-modal';

type AuditDetailsModalProps = {
  auditId: string;
  auditName: string;
  dueDateIso: string;
  isCancelling?: boolean;
  isEditable: boolean;
  isSaving?: boolean;
  onCancelAudit?: () => Promise<boolean> | boolean;
  onClose: () => void;
  onSave?: (data: AuditFormData) => Promise<boolean> | boolean;
};

export const AuditDetailsModal = ({
  auditId,
  auditName,
  dueDateIso,
  isCancelling = false,
  isEditable,
  isSaving = false,
  onCancelAudit,
  onClose,
  onSave,
}: AuditDetailsModalProps) => {
  const formattedDueDate = useUtcFormattedDate(dueDateIso);
  const [isLoading, setIsLoading] = useState(true);
  const [auditDetails, setAuditDetails] = useState<StockAuditDetailsResponse | null>(null);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);

    const loadAudit = async () => {
      try {
        const details = await getStockAudit(auditId);
        if (!isActive) {
          return;
        }
        setAuditDetails(details);
      } catch {
        if (!isActive) {
          return;
        }
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.auditDetailsLoadError} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadAudit();

    return () => {
      isActive = false;
    };
  }, [auditId]);

  const auditProducts = useMemo(() => {
    const seen = new Set<string>();
    return (auditDetails?.products ?? []).reduce<{ productId: string; sku: string; productName: string }[]>((acc, product) => {
      if (seen.has(product.productId)) {
        return acc;
      }
      seen.add(product.productId);
      acc.push({ productId: product.productId, sku: product.sku ?? '', productName: product.productName ?? '' });
      return acc;
    }, []);
  }, [auditDetails?.products]);

  const productCount = new Set(auditDetails?.products?.map(({ productId }) => productId)).size;

  const handleSave = useCallback(
    async (data: AuditFormData) => {
      const success = await onSave?.(data);
      if (success) {
        onClose();
      }
    },
    [onSave, onClose],
  );

  const handleCancelAudit = useCallback(async () => {
    const success = await onCancelAudit?.();
    if (success) {
      onClose();
    }
  }, [onCancelAudit, onClose]);

  if (isEditable) {
    if (isLoading || !auditDetails) {
      return <AuditLoadingModal onClose={onClose} titleMessage={messages.auditEditModalTitle} />;
    }

    return (
      <AuditFormModal
        captionMessage={messages.auditEditModalNotice}
        captionValues={{ count: productCount }}
        confirmLabel={messages.auditDetailsModalSaveButton}
        deleteLabel={messages.auditEditModalDeleteButton}
        initialValues={{
          auditName,
          dueDate: formattedDueDate,
          assigneeUserId: auditDetails.assigneeUserId ?? '',
          notes: auditDetails.notes ?? '',
        }}
        isCancelling={isCancelling}
        isSaving={isSaving}
        onClose={onClose}
        onConfirm={handleSave}
        onDelete={handleCancelAudit}
        products={auditProducts}
        titleMessage={messages.auditEditModalTitle}
      />
    );
  }

  return (
    <AuditReadOnlyModal
      auditDetails={auditDetails}
      auditName={auditName}
      formattedDueDate={formattedDueDate}
      isLoading={isLoading}
      onClose={onClose}
      products={auditProducts}
    />
  );
};
